import React from 'react';
import { cn } from 'shared/lib/cn';

export type DonutTone = 'brand' | 'flash';
/** One coloured segment: a medium's useable capacity as a fraction of the set's raw. */
export type DonutArc = { id: string; tone: DonutTone; fraction: number };

export type DonutProps = {
  name: string;        // "Flash Set"
  tone: DonutTone;     // colours the set name
  raw: string;         // the set's raw capacity, already formatted — the centre figure
  unit: string;        // "PB" | "PiB"
  arcs: DonutArc[];
  useable: string;     // "0.31 PB"
  detail: string;      // "10+2 · 83%"
  off?: boolean;
};

const R = 52;
const CIRC = 2 * Math.PI * R;
const GAP = 1.5; // each arc stops short so neighbours (and the ring's own start) don't touch
const STROKE: Record<DonutTone, string> = { brand: 'rgb(var(--c-brand))', flash: 'rgb(var(--c-flash))' };

/** Lays the arcs end to end, never past one full turn however the fractions arrive. */
function layout(arcs: DonutArc[]) {
  let start = 0;
  return arcs.map((arc) => {
    const fraction = isFinite(arc.fraction) ? Math.min(Math.max(arc.fraction, 0), 1 - start) : 0;
    const placed = { ...arc, fraction, start };
    start += fraction;
    return placed;
  });
}

/** One storage set's capacity ring. The coloured arcs are useable capacity by medium and
 *  the dim remainder of the track is what protection costs, so the whole ring is the set's
 *  raw capacity — the figure in the centre. Pure SVG, no state: a function of its props. */
export function Donut({ name, tone, raw, unit, arcs, useable, detail, off = false }: DonutProps) {
  const placed = off ? [] : layout(arcs);
  const filled = placed.reduce((sum, a) => sum + a.fraction, 0);
  const meter = off
    ? { role: 'img', 'aria-label': `${name}: disabled` }
    : {
      role: 'meter', 'aria-label': name,
      'aria-valuenow': Math.round(filled * 100), 'aria-valuemin': 0, 'aria-valuemax': 100,
      'aria-valuetext': `${raw} ${unit} raw, ${useable} useable, ${detail}`,
    };

  return (
    <div data-testid="donut" data-off={off} className={cn('flex min-w-0 flex-col items-center text-center transition-opacity', off && 'opacity-30')}>
      <span className={cn('mb-2 font-display text-xs font-semibold', tone === 'flash' ? 'text-flash' : 'text-brand')}>{name}</span>
      <div className="relative aspect-square w-full max-w-[132px]">
        <svg viewBox="0 0 132 132" className="block h-full w-full -rotate-90" {...meter}>
          <circle cx="66" cy="66" r={R} fill="none" stroke="rgb(var(--c-hairline) / 0.2)" strokeWidth="15" />
          {placed.map((a) => {
            const len = Math.max(0, a.fraction * CIRC - GAP);
            return (
              <circle
                key={a.id}
                data-testid="donut-arc"
                cx="66" cy="66" r={R} fill="none" stroke={STROKE[a.tone]} strokeWidth="15"
                strokeDasharray={`${len.toFixed(2)} ${(CIRC - len).toFixed(2)}`}
                strokeDashoffset={(-(a.start * CIRC)).toFixed(2)}
              />
            );
          })}
        </svg>
        <div className="absolute inset-0 grid place-content-center" aria-hidden="true">
          <span className="font-code text-lg font-semibold leading-none text-ink">{off ? '—' : raw}</span>
          <span className="mt-1 font-body text-tiny text-ink-muted">Raw {unit}</span>
        </div>
      </div>
      <div className="mt-2 flex flex-col font-body text-tiny text-ink-muted">
        {off ? <span>Disabled</span> : (
          <>
            <span>useable <span className="text-ink">{useable}</span></span>
            <span>{detail}</span>
          </>
        )}
      </div>
    </div>
  );
}
